import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconMoodSad, IconLoader2, IconX } from '@tabler/icons-react';
import {
    Dialog, DialogContent, DialogClose, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

// Predefined dislike reasons. The `key` is what gets stored on the server
// (AiTemplateImage.feedbackReason); the label is translated. "other" reveals the
// free-text note box below.
const REASONS = ['face_not_matching', 'bg_not_changing', 'color_not_applying', 'low_quality', 'other'];
// The photo editors' list (AI photo editor, Studio Editor; order matters). Keys must exist in
// worldCupJersey.feedback.reasons.* AND in the backend ALLOWED_FEEDBACK_REASONS, or the reason is dropped server-side.
export const IMAGE_FEEDBACK_REASONS = ['face_not_matching', 'face_spots', 'download_issue', 'size_issue', 'need_smoother', 'tools_not_working', 'other'];

const NOTE_MAX_CHARS = 50;
const NOTE_MAX_WORDS = 20;

// In `strict` mode, keep only letters (any language incl. Bengali), the combining
// marks that letters need (\p{M} — Bengali vowel signs ে ু ি … and the hasant ্ that
// forms conjuncts; without it "প্রশ্ন" would corrupt to "পরশন"), digits, whitespace and
// a small set of sentence punctuation ( । , . ? ! ) — every other symbol, bracket, math
// sign or emoji is dropped. This is the "no special characters" rule.
const STRICT_DISALLOWED = /[^\p{L}\p{M}\p{N}\s।.,?!]/gu;

// Sanitize the free-text note so no dangerous/script content can be typed or pasted,
// then clamp to the char + word limits. Returns a safe, bounded string. Bengali/English
// text is preserved; `strict` additionally strips non-alphanumeric symbols.
function sanitizeNote(raw, maxWords = NOTE_MAX_WORDS, strict = false) {
    let v = (raw || '');
    v = v.replace(/[\x00-\x1F\x7F]/g, ' ');           // strip control chars / null bytes
    v = v.replace(/[<>]/g, '');                              // no tags: <script>, <img>, etc.
    v = v.replace(/&(?:#x?[0-9a-f]+|[a-z]+);/gi, '');        // strip HTML entities (&lt; &#60; …)
    v = v.replace(/(?:javascript|data|vbscript)\s*:/gi, ''); // strip risky URI schemes
    v = v.replace(/on\w+\s*=/gi, '');                        // strip inline event handlers (onclick=)
    v = v.replace(/[\u2028\u2029]/g, ' ');             // strip line/paragraph separators
    if (strict) v = v.replace(STRICT_DISALLOWED, '');       // no special characters
    if (v.length > NOTE_MAX_CHARS) v = v.slice(0, NOTE_MAX_CHARS);
    const words = v.split(/\s+/).filter(Boolean);
    if (words.length > maxWords) v = words.slice(0, maxWords).join(' ');
    return v;
}

/**
 * Collects a reason (+ optional note) when a user dislikes a generated AI-template
 * result. Submits { reason, note } back to the parent, which posts it as negative
 * feedback. Reason is required; note is optional (but encouraged for "other").
 */
export default function AiTemplateFeedbackModal({ open, onOpenChange, onSubmit, submitting = false, maxWords = NOTE_MAX_WORDS, strict = false, reasons = REASONS }) {
    const { t } = useTranslation();
    const [reason, setReason] = useState(null);
    const [note, setNote] = useState('');

    // Reset the form each time the modal opens so a previous pick doesn't linger.
    useEffect(() => {
        if (open) { setReason(null); setNote(''); }
    }, [open]);

    const canSubmit = !!reason && !submitting;
    const wordCount = note.trim() ? note.trim().split(/\s+/).length : 0;

    // Switching to a non-"other" reason discards any note (the box is hidden for it).
    const selectReason = (key) => {
        setReason(key);
        if (key !== 'other') setNote('');
    };

    return (
        <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
            <DialogContent className="sm:max-w-md rounded-2xl" showClose={false}>
                {/* Red close button (replaces the default grey X) */}
                <DialogClose
                    disabled={submitting}
                    aria-label={t('worldCupJersey.feedback.cancel')}
                    className="absolute right-4 top-4 inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/40 disabled:opacity-50 disabled:pointer-events-none"
                >
                    <IconX size={15} />
                    <span className="sr-only">{t('worldCupJersey.feedback.cancel')}</span>
                </DialogClose>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-zinc-800 dark:text-zinc-100">
                        <span className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                            <IconMoodSad size={18} className="text-red-500" />
                        </span>
                        {t('worldCupJersey.feedback.dislikeTitle')}
                    </DialogTitle>
                    <DialogDescription className="text-zinc-500 dark:text-zinc-400">
                        {t('worldCupJersey.feedback.dislikeSubtitle')}
                    </DialogDescription>
                </DialogHeader>

                {/* Reason options */}
                <div className="flex flex-col gap-2 mt-1">
                    {reasons.map((key) => {
                        const active = reason === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => selectReason(key)}
                                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                                    active
                                        ? 'border-primary bg-primary/10 text-zinc-800 dark:text-zinc-100'
                                        : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                                }`}
                            >
                                <span className={`w-4 h-4 rounded-full flex-shrink-0 border-[1.5px] transition-colors ${
                                    active ? 'border-[4.5px] border-primary' : 'border-zinc-300 dark:border-zinc-600'
                                }`} />
                                {t(`worldCupJersey.feedback.reasons.${key}`)}
                            </button>
                        );
                    })}
                </div>

                {/* Free-text note — only for "Other". Sanitized + bounded. */}
                {reason === 'other' && (
                    <div className="mt-1">
                        <textarea
                            value={note}
                            onChange={(e) => setNote(sanitizeNote(e.target.value, maxWords, strict))}
                            onPaste={(e) => {
                                // Sanitize pasted content too (paste can inject > limit / tags).
                                e.preventDefault();
                                const text = (e.clipboardData || window.clipboardData).getData('text');
                                setNote(prev => sanitizeNote(prev + text, maxWords, strict));
                            }}
                            rows={3}
                            maxLength={NOTE_MAX_CHARS}
                            placeholder={t('worldCupJersey.feedback.notePlaceholder')}
                            className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        />
                        <p className="mt-1 text-[11px] text-zinc-400 dark:text-zinc-500 text-right tabular-nums">
                            {t('worldCupJersey.feedback.noteLimit', { chars: note.length, maxChars: NOTE_MAX_CHARS, words: wordCount, maxWords })}
                        </p>
                    </div>
                )}

                <div className="flex gap-2 mt-1">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        className="flex-1 px-4 py-2.5 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 text-sm font-medium rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                    >
                        {t('worldCupJersey.feedback.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={() => onSubmit(reason, note.trim())}
                        disabled={!canSubmit}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting && <IconLoader2 size={16} className="animate-spin" />}
                        {t('worldCupJersey.feedback.submit')}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
